import React from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import { Row, Col } from "react-bootstrap";
import Hero from "src/components/Hero/Hero";
import NavTabs from "src/components/Poker/Tournaments/NavTabs";
import TournamentThreeColumnImages from "./TournamentThreeColumnImages";
import TournamentContentSection from "./TournamentContentSection";
import TournamentHighlightedList from "./TournamentHighlightedList";
import TournamentVideoSection from "./TournamentVideoSection";
import TournamentDropDownText from "./TournamentDropDownText";
import TournamentPrizePoolTable from "./TournamentPrizePoolTable";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import TournamentHotelCard from "./TournamentHotelCard";
import TournamentTravelDiscount from "./TournamentTravelDiscount";
import TournamentSubtabsContent from "./TournamentSubtabsContent";
import TournamentSectionalTabs from "src/components/SectionalTabs/TournamentSectionalTabs";
import TournamentEventContent from "./TournamentEventContent";
import TournamentDynamicTable from "./TournamentDynamicTable";
import TournamentImageSection from "./TournamentImageSection";
import TitleSection from "src/components/TitleSection/TitleSection";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import Button from "src/components/Button/Button";
import CoverageAndResultsBanner from "src/components/Poker/Tournaments/CoverageAndResultsBanner";
import EventsTable from "../EventsTable";
import LeaderboardTable from "../LeaderboardTable";
import QualifiedLeaderboardTable from "../QualifiedLeaderboardTable";
import SeatsGuaranteedTable from "../SeatsGuarenteedTable";
import EventFinished from "src/components/EventFinished";
import TournamentTwoColumnCards from "./TournamentTwoColumnCards";
import TournamentShowcaseCard from "./TournamentShowcaseCard";
import TournamentEventList from "./TournamentEventList";
import TournamentPromoBanner from "./TournamentPromoBanner";
import TournamentAppSection from "./TournamentAppSection";
import TournamentAppPhonePanels from "./TournamentAppPhonePanels";
import ImageCarousel from "src/components/ImageCarousel/ImageCarousel";
import ResponsiveImageGrid from "src/components/ResponsiveImageGrid/ResponsiveImageGrid";

// Wrapper components for Row and Col that support nested sections
const RowWrapper = ({ sections = [], renderSection, className, ...props }) => (
  <Row className={className} {...props}>
    {sections.map((section, index) => renderSection(section, index))}
  </Row>
);

const ColWrapper = ({ sections = [], renderSection, ...props }) => (
  <Col {...props}>
    {sections.map((section, index) => renderSection(section, index))}
  </Col>
);

// Wrapper component for full-width sections using existing container-fluid
const ContainerFluidWrapper = ({ sections = [], renderSection }) => (
  <container-fluid>
    {sections.map((section, index) => renderSection(section, index))}
  </container-fluid>
);

// Component registry - maps component type strings to actual components
const COMPONENT_MAP = {
  Hero: Hero,
  NavTabs: NavTabs,
  EventFinished: EventFinished,
  CoverageAndResultsBanner: CoverageAndResultsBanner,
  ResponsiveImage: ResponsiveImage,
  ThreeColumnImages: TournamentThreeColumnImages,
  ContentSection: TournamentContentSection,
  TitleSection: TitleSection,
  SubTitleSection: SubTitleSection,
  Row: RowWrapper,
  Col: ColWrapper,
  ContainerFluid: ContainerFluidWrapper,
  ImageCarousel: ImageCarousel,
  ResponsiveImageGrid: ResponsiveImageGrid,
  TextContent: ({ content, variant }) => (
    <ContentText variant={variant}>
      <ReactMarkdown>{content}</ReactMarkdown>
    </ContentText>
  ),
  CenteredTextContent: ({ content }) => (
    <ContentCenteredText>
      <ReactMarkdown>{content}</ReactMarkdown>
    </ContentCenteredText>
  ),
  SmallTextContent: ({ content }) => (
    <ContentTextSmall>
      <ReactMarkdown>{content}</ReactMarkdown>
    </ContentTextSmall>
  ),
  HotelCard: TournamentHotelCard,
  TravelDiscount: TournamentTravelDiscount,
  GridContent: ({ grid }) => (
    <GridContent text={grid?.textSticker}>
      <ResponsiveImage src={grid?.image} alt="check-icon" />
      <div>
        <ReactMarkdown>{grid?.text}</ReactMarkdown>
        {grid?.specialText && (
          <SpecialText>
            <ReactMarkdown>{grid?.specialText?.left}</ReactMarkdown>
            <ReactMarkdown>{grid?.specialText?.right}</ReactMarkdown>
          </SpecialText>
        )}
      </div>
    </GridContent>
  ),
  EventContent: TournamentEventContent,
  HighlightedList: TournamentHighlightedList,
  DynamicTable: TournamentDynamicTable,
  VideoSection: TournamentVideoSection,
  ImageSection: TournamentImageSection,
  DropDownText: TournamentDropDownText,
  PrizePoolTable: TournamentPrizePoolTable,
  EventsTable: EventsTable,
  LeaderboardTable: LeaderboardTable,
  QualifiedLeaderboardTable: QualifiedLeaderboardTable,
  SeatsGuaranteedTable: SeatsGuaranteedTable,
  ImageTextWrapper: ({ imageTextWrapper }) => (
    <ImageTextWrapper>
      <ResponsiveImage src={imageTextWrapper?.image} alt="text-image" />
      <ReactMarkdown>{imageTextWrapper?.text}</ReactMarkdown>
    </ImageTextWrapper>
  ),
  SectionalTabs: TournamentSectionalTabs,
  SubtabsContent: TournamentSubtabsContent,
  Button: Button,
  TwoColumnCards: TournamentTwoColumnCards,
  ShowcaseCard: TournamentShowcaseCard,
  EventList: TournamentEventList,
  PromoBanner: TournamentPromoBanner,
  AppSection: TournamentAppSection,
  AppPhonePanels: TournamentAppPhonePanels,
};

/**
 * TournamentPageRenderer - Dynamically renders tournament page sections based on JSON configuration
 *
 * @param {Object} props
 * @param {Array} props.sections - Array of section configurations with type and props
 * @param {string} props.variant - Page variant (e.g., 'dark' or 'light')
 * @param {boolean} props.hasBgImage - Whether parent container has a background image
 * @param {Object} props.additionalProps - Additional props to pass to specific components (e.g., tournamentsData, leaderboardData)
 */
const TournamentPageRenderer = ({
  sections = [],
  variant,
  hasBgImage = false,
  fullWidthOnDesktop = false,
  additionalProps = {},
}) => {
  const renderSection = (section, index) => {
    const { type, props = {} } = section;

    // Get the component from the registry
    const Component = COMPONENT_MAP[type];

    if (!Component) {
      console.warn(`Component type "${type}" not found in registry`);
      return null;
    }

    // Only pass additionalProps to components that might need them
    // Most components receive their props directly from the JSON configuration
    const componentsThatNeedAdditionalProps = [
      "EventsTable",
      "LeaderboardTable",
      "QualifiedLeaderboardTable",
      "SubtabsContent",
    ];

    // Components that need renderSection passed to them for nested sections
    const componentsThatNeedRenderSection = [
      "Row",
      "Col",
      "ContainerFluid",
      "ShowcaseCard",
    ];

    const mergedProps = componentsThatNeedAdditionalProps.includes(type)
      ? { ...props, ...additionalProps }
      : props;

    // Pass renderSection to components that support nested sections
    if (componentsThatNeedRenderSection.includes(type)) {
      return (
        <Component
          key={`${type}-${index}`}
          {...mergedProps}
          renderSection={renderSection}
        />
      );
    }

    return <Component key={`${type}-${index}`} {...mergedProps} />;
  };

  // Group sections by whether they need full-width or not
  const renderContent = () => {
    const content = [];
    let currentGroup = [];

    sections.forEach((section, index) => {
      if (section.type === "ContainerFluid") {
        // Render accumulated sections in container-div
        if (currentGroup.length > 0) {
          content.push(
            <container-div key={`container-${content.length}`}>
              {currentGroup}
            </container-div>,
          );
          currentGroup = [];
        }
        // Render full-width section outside container-div
        content.push(renderSection(section, index));
      } else {
        currentGroup.push(renderSection(section, index));
      }
    });

    // Render remaining sections
    if (currentGroup.length > 0) {
      content.push(
        <container-div key={`container-${content.length}`}>
          {currentGroup}
        </container-div>,
      );
    }

    return content;
  };

  return (
    <Div variant={variant} hasBgImage={hasBgImage} fullWidthOnDesktop={fullWidthOnDesktop} className={variant}>
      {renderContent()}
    </Div>
  );
};

// Styled components
const Div = styled.div`
  padding-top: 20px;
  background-color: ${(props) =>
    props.hasBgImage
      ? "transparent"
      : props.variant === "dark"
        ? "var(--lightblack)"
        : "#fff"};
  color: ${(props) => (props.variant === "dark" ? "#fff" : "#000")};
  
  ${(props) =>
    props.fullWidthOnDesktop &&
    `
    container-fluid {
      padding-left: 0;
      padding-right: 0;
    }
  `}
  
  .title-section {
    h2 {
      padding: 0;
      text-wrap: balance;
    }
    p {
      padding: 0;
    }
  }
  h4 {
    margin: 27px 0 5px;
  }
  p {
    margin: 20px 0 10px;
  }
`;

const ContentText = styled.div`
  p {
    padding: 0 20px 20px 20px;
    font-size: 18px;
    line-height: 26px;
    margin: 0px auto;
  }
`;

const ContentCenteredText = styled.div`
  text-align: center !important;
  p {
    padding: 0 20px 20px 20px;
    font-size: 18px;
    line-height: 26px;
    margin: 0px auto;
  }
`;

const ContentTextSmall = styled.div`
  text-align: center;
`;

const GridContent = styled.div`
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin: 20px 0;
  li {
    text-align: left;
  }
  img {
    max-width: 470px;
  }
  div {
    max-width: 470px;
    position: relative;
    overflow: hidden;
    &:after {
      content: "${({ text }) => text}";
      position: absolute;
      top: 42px;
      right: -110px;
      font-size: 55px;
      color: rgba(255, 255, 255, 0.8);
      text-transform: uppercase;
      transform: rotate(40deg);
      background: rgba(205, 0, 0, 0.8);
      width: 400px;
      text-align: center;
    }
  }
`;

const SpecialText = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row wrap;
  justify-content: flex-start;
  gap: 10px;
  align-items: center;
  float: left;
  background: #2d2c34;
  border: solid 1px #cc222a;
  color: var(--white);
  text-align: left;
  padding: 0 15px;
  p:first-child {
    color: #cc222a;
    font-size: 60px;
    font-weight: 700;
    padding: 0px;
  }
`;

const ImageTextWrapper = styled.div`
  margin: 20px 0;
  width: 100%;
  margin: 0 auto 40px auto;
  position: relative;
  z-index: 1;

  img {
    width: 100%;
    min-height: 200px;
    object-fit: cover;
  }

  p {
    position: absolute;
    padding: 10px 30px;
    width: 100%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    color: white;
    font-weight: normal;
    text-align: center;
  }
  @media (max-width: 768px) {
    p {
      padding: 15px 20px;
      font-size: 14px;
    }
  }
`;

export default TournamentPageRenderer;
export { COMPONENT_MAP };
